import { useMemo } from 'react';
import { Dropdown, DropdownItem, DropdownToggle } from 'reactstrap';
import styled from 'styled-components';
import DotMenuIcon from '../icons/DotMenuIcon';
import { useToggleState } from '../../utils/hooks';
import SmallFontDropdownMenu from '../basics/SmallFontDropdownMenu';
import {
  HoverNavItem,
  IconSpan,
  StyledNameDiv,
} from '../left-menu/menu-components';
import { captureEvent } from '../../utils/analytics';
import { usePatternEditorContext } from '../../contexts/PatternEditorContext';
import { SectionItem } from '../left-menu/FieldsTree';
import { alphanumericSort } from '../../utils/utils';
import PatternIcon from '../icons/PatternIcon';
import { useItemContext } from '../../contexts/ItemContext';

const IndentedSpan = styled(IconSpan)`
  display: flex;
  justify-content: center;
  margin-left: 11px; // Indent same amount as sections
  padding: 6px 0;
  color: var(--text-muted-color);
`;

const MaxWidthNameDiv = styled(StyledNameDiv)`
  input {
    max-width: calc(var(--side-panel-width) - 45px);
  }
`;

const StyledDropdown = styled(Dropdown)`
  .dropdown-menu {
    max-width: calc(var(--side-panel-width) - 10px);
    min-width: 200px;
    max-height: 200px;
    overflow: scroll;
  }
`;

const PatternUsageDropdownItem = ({ pattern }) => {
  const { id, name } = pattern;
  const { activeKey, setActiveKey, clearActiveKey } = useItemContext();
  const { setEditorPattern, doOrConfirmIfDirty } = usePatternEditorContext();
  const onClick = () => {
    captureEvent('pattern-viewed-existing', {
      location: 'crop usage panel',
    });
    doOrConfirmIfDirty(() => {
      setEditorPattern(pattern);
    });
  };

  return (
    <HoverNavItem
      className="d-flex align-items-center"
      hovered={activeKey === id}
      onClick={onClick}
      onMouseEnter={() => setActiveKey(id)}
      onMouseLeave={clearActiveKey}
    >
      <IndentedSpan>
        <PatternIcon />
      </IndentedSpan>
      <MaxWidthNameDiv title={name}>{name}</MaxWidthNameDiv>
    </HoverNavItem>
  );
};

const SectionUsageDropdownItem = ({ rowIndices = [], section }) => {
  const filterRows = (_row, i) => rowIndices.includes(i);
  return <SectionItem section={section} filterRows={filterRows} />;
};

const CropUsagePanel = ({ crop, patterns = [], sections = [] }) => {
  const [dropdownOpen, toggleDropdownOpen] = useToggleState();
  const sortedPatterns = useMemo(
    () =>
      patterns.sort((a, b) => alphanumericSort(a.pattern.name, b.pattern.name)),
    [patterns],
  );
  const sortedSections = useMemo(
    () =>
      sections.sort((a, b) => alphanumericSort(a.section.name, b.section.name)),
    [sections],
  );

  if (!patterns.length && !sections.length) return null;

  return (
    <StyledDropdown
      id={`crop-usage-panel-${crop.id || crop.name}`}
      isOpen={dropdownOpen}
      toggle={toggleDropdownOpen}
    >
      <DropdownToggle className="p-0 px-1" color="link" title="Crop Usage">
        <DotMenuIcon />
      </DropdownToggle>
      <SmallFontDropdownMenu positionFixed>
        {!!sections.length && (
          <>
            <DropdownItem header>Sections</DropdownItem>
            {sortedSections.map(({ rowIndices, section }) => (
              <SectionUsageDropdownItem
                key={section.id}
                rowIndices={rowIndices}
                section={section}
              />
            ))}
          </>
        )}
        {!!patterns.length && !!sections.length && <DropdownItem divider />}
        {!!patterns.length && (
          <>
            <DropdownItem header>Patterns</DropdownItem>
            {sortedPatterns.map(({ pattern }) => (
              <PatternUsageDropdownItem key={pattern.id} pattern={pattern} />
            ))}
          </>
        )}
      </SmallFontDropdownMenu>
    </StyledDropdown>
  );
};

export default CropUsagePanel;
